import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Divider,
  Chip,
  Pagination,
} from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { database } from '../firebase';
import { ref, push, set, onValue } from 'firebase/database';
import { activityLogService } from '../services/activityLogService';

interface SupportTicketEntry {
  supportTicketID: string;
  clientUserID: string;
  responderUserID?: string;
  timestamp: number;
  comment: string;
  status: string;
  id?: string; // Firebase key
}

interface GroupedTicket {
  supportTicketID: string;
  entries: SupportTicketEntry[];
  firstTimestamp: number;
}

const Support: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: userData } = useUser();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tickets, setTickets] = useState<GroupedTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const ticketsPerPage = 5;

  // Fetch tickets for the logged-in user
  useEffect(() => {
    if (!userData?.uid) {
      setLoading(false);
      return;
    }

    const feedBackRef = ref(database, 'feedBack');
    
    // Listen for changes to feedback entries
    const unsubscribe = onValue(feedBackRef, (snapshot) => {
      if (!snapshot.exists()) {
        setTickets([]);
        setLoading(false);
        return;
      }

      const allEntries: SupportTicketEntry[] = [];
      snapshot.forEach((childSnapshot) => {
        const entry = childSnapshot.val();
        // Filter entries where clientUserID matches the logged-in user
        if (entry.clientUserID === userData.uid) {
          allEntries.push({
            ...entry,
            id: childSnapshot.key || undefined,
          });
        }
      });

      // Group entries by supportTicketID
      const groupedMap = new Map<string, SupportTicketEntry[]>();
      allEntries.forEach((entry) => {
        const ticketID = entry.supportTicketID;
        if (!groupedMap.has(ticketID)) {
          groupedMap.set(ticketID, []);
        }
        groupedMap.get(ticketID)!.push(entry);
      });

      // Convert to array and sort entries within each group by timestamp
      const grouped: GroupedTicket[] = Array.from(groupedMap.entries()).map(([ticketID, entries]) => {
        // Sort entries by timestamp (oldest first)
        entries.sort((a, b) => a.timestamp - b.timestamp);
        return {
          supportTicketID: ticketID,
          entries,
          firstTimestamp: entries[0]?.timestamp || 0,
        };
      });

      // Sort groups by first timestamp (newest first)
      grouped.sort((a, b) => b.firstTimestamp - a.firstTimestamp);

      setTickets(grouped);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching support tickets:', error);
      setErrorMessage('Failed to load support tickets. Please try again.');
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [userData?.uid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userData?.uid) {
      setErrorMessage('User not authenticated. Please log in.');
      return;
    }

    if (!comment.trim()) {
      setErrorMessage('Please enter a comment.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      // Generate a unique support ticket ID
      const feedBackRef = ref(database, 'feedBack');
      const newEntryRef = push(feedBackRef);
      const supportTicketID = newEntryRef.key || `ticket_${Date.now()}`;

      // Create the support ticket entry
      const ticketEntry: Omit<SupportTicketEntry, 'id'> = {
        supportTicketID,
        clientUserID: userData.uid,
        responderUserID: '',
        timestamp: Date.now(),
        comment: comment.trim(),
        status: 'Submitted',
      };

      await set(newEntryRef, ticketEntry);

      // Log the activity
      try {
        await activityLogService.logActivity({
          timestamp: Date.now(),
          uid: userData.uid,
          actionTaken: 'Submitted',
          actionSubject: 'Support Ticket',
        });
      } catch (error) {
        console.error('Error logging support ticket activity:', error);
        // Don't block the flow if logging fails
      }

      // Clear the form
      setComment('');
      setSuccessMessage(true);
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      setErrorMessage('Failed to submit support ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(false);
  };

  const handleCloseError = () => {
    setErrorMessage(null);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'submitted':
        return 'info';
      case 'in progress':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(tickets.length / ticketsPerPage);
  const startIndex = (page - 1) * ticketsPerPage;
  const endIndex = startIndex + ticketsPerPage;
  const paginatedTickets = tickets.slice(startIndex, endIndex);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Support
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        From time to time, you may find a broken link or something may not work as you expected. Please help us maintain a high quality user experience by letting us know when something doesn't look right or if you have a feature request. We do our best to respond within 24 hours and often resolve things much faster.
      </Typography>

      {/* Submit Ticket Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Submit a Support Ticket
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Enter your comment or question"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Describe your issue or question here..."
              disabled={submitting}
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !comment.trim()}
              sx={{ minWidth: 120 }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Ticket'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Ticket History */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Ticket History
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : paginatedTickets.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
              No support tickets found. Submit a ticket using the form above.
            </Typography>
          ) : (
            <>
              {paginatedTickets.map((ticket, ticketIndex) => (
                <Box key={ticket.supportTicketID} sx={{ mb: 3 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Ticket ID: {ticket.supportTicketID}
                    </Typography>
                    <Chip
                      label={ticket.entries[ticket.entries.length - 1]?.status || 'Unknown'}
                      color={getStatusColor(ticket.entries[ticket.entries.length - 1]?.status || '') as any}
                      size="small"
                    />
                  </Box>

                  {ticket.entries.map((entry, entryIndex) => (
                    <Box key={entry.id || entryIndex} sx={{ mb: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(entry.timestamp)}
                        </Typography>
                        {entry.responderUserID && (
                          <Chip
                            label="Response"
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {entry.comment}
                      </Typography>
                      {entryIndex < ticket.entries.length - 1 && (
                        <Divider sx={{ mt: 2 }} />
                      )}
                    </Box>
                  ))}

                  {ticketIndex < paginatedTickets.length - 1 && (
                    <Divider sx={{ mt: 3, mb: 3 }} />
                  )}
                </Box>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Support ticket submitted successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={5000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseError} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Support;

